import { call, delay, put, race, select, takeEvery } from 'redux-saga/effects';
import { isNil } from 'ramda';
import ErrorCodes, {
    INTERNAL_TIMEOUT_ERROR_PAYLOAD,
    INTERNAL_UNCAUGHT_ERROR_PAYLOAD,
} from 'mangools-commons/dist/constants/ErrorCodes';
import Urls from 'mangools-commons/dist/constants/Urls';

import ActionTypes from 'constants/ActionTypes';
import Strings from 'constants/Strings';
import TrackingKeywordsRefreshSource from 'sources/TrackingKeywordsRefreshSource';

import { accessTokenSelector } from 'selectors/userSelectors';

import {
    closeRefreshRankingsConfirmMessage,
    showAccessDeniedMessage,
    showNoConnectionMessage,
    showRefreshRankingsSuccessMessage,
    showUnauthorizedMessage,
    addRefreshingKeywordIds,
    showFailureMessage,
} from 'actions/uiActions';

import {
    receivedOnDemandRefreshAction,
    errorOnDemandRefreshAction,
} from 'actions/dataActions';

import { requestedLimitsAction } from 'actions/userActions';
import { gtmTrack } from 'actions/analyticsActions';
import { handleUncaught, logError } from 'sagas/errorSagas';

import { analyticsEvents } from 'constants/analytics';

const MAX_REQUEST_TIMEOUT = 60 * 1000;

const onDemandRefresh = handleUncaught(
    function* onDemandRefresh(action) {
        const { trackingId, keywordIds } = action.payload;
        const accessToken = yield select(accessTokenSelector);

        // NOTE: the keywords are marked as refreshing only once the server has said which of them it
        // actually accepted. Marking the whole selection up front paints spinners on keywords the server
        // then skips, and on every keyword of a request that fails outright.
        yield put(closeRefreshRankingsConfirmMessage());

        const { result, _timeout } = yield race({
            result: call(TrackingKeywordsRefreshSource.refresh, accessToken, { trackingId, keywordIds }),
            _timeout: delay(MAX_REQUEST_TIMEOUT),
        });

        if (isNil(result)) {
            // The POST is already on its way out and may well have spent the credits, so the message
            // must not promise the user that nothing happened.
            yield put(errorOnDemandRefreshAction(INTERNAL_TIMEOUT_ERROR_PAYLOAD));
            yield put(showFailureMessage({ details: Strings.messages.failure.on_demand_refresh_timeout }));
            yield call(logError, 'OnDemandRefreshSaga', INTERNAL_TIMEOUT_ERROR_PAYLOAD);
        } else if (result.error) {
            // Nothing was marked as refreshing before the response arrived, so there is nothing to clear
            // here - and clearing would wipe the spinners of a refresh that is still running.
            yield put(errorOnDemandRefreshAction(result.payload));

            // Deliberately NOT routed through `handleError`: its FETCH_ERROR branch re-fires the request
            // after a delay, and this POST spends credits. The other statuses map to the same modals it
            // would have picked.
            switch (result.payload.status) {
                case ErrorCodes.FETCH_ERROR: {
                    yield put(showNoConnectionMessage());
                    break;
                }
                case ErrorCodes.UNAUTHORIZED: {
                    yield put(showUnauthorizedMessage());
                    break;
                }
                case ErrorCodes.ACCESS_DENIED: {
                    yield put(showAccessDeniedMessage());
                    break;
                }
                case ErrorCodes.TOO_MANY_REQUESTS: {
                    // Out of on-demand rank scans: the API answers 429 with `type: 'CreditLimit'` and the
                    // counts in `details`. The refresh endpoint has no other 429, so the status is enough.
                    // Nothing was consumed by a 429, so refetching the limits here is safe - and it is
                    // what corrects the stale counter that let the click through in the first place.
                    yield put(requestedLimitsAction());

                    // `requested` is counted in scans, not keywords, so the sentence stays true whatever the
                    // user's multiplier. A 429 without both counts keeps the static text.
                    const { requested, remaining } = result.payload.details ?? {};
                    const hasCounts = Number.isFinite(requested) && Number.isFinite(remaining);

                    yield put(
                        showFailureMessage({
                            header: Strings.messages.failure.on_demand_refresh_no_credits_header,
                            details: hasCounts
                                ? Strings.messages.failure.on_demand_refresh_no_credits_counts(requested, remaining)
                                : Strings.messages.failure.on_demand_refresh_no_credits,
                            footer: Strings.messages.failure.on_demand_refresh_no_credits_footer,
                            // Same destination and ref as the tooltip on the disabled "Refresh today's ranks" button.
                            link: {
                                href: `${Urls.MANGOOLS_PLANS_AND_PRICING_URL}?ref=kw-rescan-sw`,
                                label: Strings.messages.failure.on_demand_refresh_no_credits_link,
                            },
                        }),
                    );
                    break;
                }
                default: {
                    yield put(showFailureMessage({ details: Strings.messages.failure.on_demand_refresh_error }));
                    yield call(logError, 'OnDemandRefreshSaga', result.payload);
                }
            }
        } else {
            const { refreshedCount, skippedCount, estimateSeconds, refreshedKeywordIds } = result.payload;

            // `refreshedCount` is the server's post-skip number, which is what the epic asks the event to
            // carry. Sent unconditionally: the spec fires it "once we calculate how many keywords will be
            // refreshed", and an all-skipped click is exactly the case analytics wants to see.
            // `rescanned_kws_count` is snake_case because that is the parameter name in the GTM container.
            yield put(
                gtmTrack({
                    event: analyticsEvents.KWS_RESCANNED,
                    rescanned_kws_count: refreshedCount,
                }),
            );

            // Carries `remaining` into state.user.limits so the next click sees the post-spend count.
            yield put(receivedOnDemandRefreshAction(result.payload));

            // Only the keywords the server actually took are marked as refreshing; skipped ones are done.
            // This ADDS to whatever a still-running earlier refresh put there - the epic explicitly allows
            // a second refresh while the first is in flight, and replacing the list would stop the first
            // batch's spinners early. The whole list is cleared when the tracking is reloaded.
            if (refreshedKeywordIds.length > 0) {
                yield put(addRefreshingKeywordIds(refreshedKeywordIds));
            }

            yield put(showRefreshRankingsSuccessMessage({ refreshedCount, skippedCount, estimateSeconds }));
        }
    },
    // Without this an unexpected exception kills the `takeEvery` watcher and every later click on
    // "Refresh now" does nothing at all. The throw can happen after a paid HTTP 200, so the message
    // stays neutral about whether the ranks were refreshed.
    function* onError() {
        yield put(errorOnDemandRefreshAction(INTERNAL_UNCAUGHT_ERROR_PAYLOAD));
        yield put(showFailureMessage({ details: Strings.messages.failure.on_demand_refresh_unknown }));
    },
);

function* watchOnDemandRefreshRequests() {
    // takeEvery, not takeLatest: the epic explicitly lets the user start a second refresh while the
    // first is still running. takeLatest would cancel the first saga after its POST had already
    // left, consuming the credits with no modal and no keywords marked as refreshing.
    yield takeEvery(ActionTypes.DATA_ON_DEMAND_REFRESH_REQUESTED, onDemandRefresh);
}

export default watchOnDemandRefreshRequests;
